import { optimisticChannelsReadStatusAtom, userInfoAtom } from 'app/atoms';
import type { TopLoadingIndicatorState } from 'app/components/top-loading-indicator';
import { useTopLoadingIndicator } from 'app/components/top-loading-indicator';
import { useTutorials } from 'app/components/tutorial-overlay';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Gradient } from 'app/design/primitives/gradient';
import { Accessory } from 'app/design/primitives/page/accessory';
import { ListPage, type ListPageProps } from 'app/design/primitives/page/list';
import { PageListComponentType } from 'app/design/primitives/page/list.constants';

import { useNavigation, useNavigationOptions, useRoute } from 'app/hooks/navigation';
import {
  NO_CHANNEL_SELECTED,
  useAllChannels,
  useBatchArchiveChannels,
  useBatchUnreadChannels,
  useMarkChannelAsRead,
} from 'app/network/chat/channel';
import { useOnFocus } from 'app/services/on-focus';
import {
  type Channel,
  CustomChannelType,
  type ChannelListItem as ChannelListItemType,
  type ChannelGroupHeader,
  isGroupHeader,
} from 'app/types/chat';
import { Tutorial } from 'app/types/tutorial';
import { isMobileNative, useUISize } from 'app/utils/web-helpers';
import { useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ChannelListItem } from './channel-item';
import { useTranslation } from 'react-i18next';
import { useMessageFooterContext } from 'app/screens/chat/message-footer-context';
import { ListingType, type Listing } from 'app/types/listing';
import { ChannelListHeader } from './channel-list-header';
import { GroupedChannels } from './grouped-channels';
import { createTheme as createDSTheme } from 'app/shopify';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface ChannelListProps extends Omit<ListPageProps<ChannelListItemType>, 'data' | 'renderItem'> {
  search?: string;
  archived?: boolean;
  isLoadingState: TopLoadingIndicatorState;
  isSelecting?: boolean;
  exitEditMode?: () => void;
  listingId?: Listing['id'];
  userId?: string;
  count?: number;
  onGroupHeaderPress?: (filter: { listingId?: Listing['id']; userId?: string; count: number }) => void;
}

const useChannelSelectionState = (isSelecting: boolean | undefined) => {
  const [selectionState, setSelectionState] = useState({
    isSelecting,
    selectedChannels: [] as Channel['channel_url'][],
  });
  let currentSelectionState = selectionState;
  if (selectionState.isSelecting !== isSelecting) {
    currentSelectionState = { isSelecting, selectedChannels: [] };
    setSelectionState(currentSelectionState);
  }
  const setSelectedChannels: Dispatch<SetStateAction<Channel['channel_url'][]>> = (nextValue) => {
    setSelectionState((current) => {
      const selectedChannels = current.isSelecting === isSelecting ? current.selectedChannels : [];
      return {
        isSelecting,
        selectedChannels: typeof nextValue === 'function' ? nextValue(selectedChannels) : nextValue,
      };
    });
  };
  return { selectedChannels: currentSelectionState.selectedChannels, setSelectedChannels };
};

const getChannelListItems = (
  flatItems: ChannelListItemType[],
  isSelecting: boolean | undefined,
  selectedChannels: Channel['channel_url'][],
) => {
  if (!isSelecting) return flatItems;
  const selectedChannelUrls = new Set(selectedChannels);
  return flatItems
    .filter((item): item is Channel => !isGroupHeader(item))
    .map((item) => ({
      ...item,
      isSelected: selectedChannelUrls.has(item.channel_url),
    }));
};

const ChannelSelectionAccessory = ({
  archiveAction,
  layout,
  unreadAction,
  t,
}: {
  archiveAction: { availability: 'enabled' | 'disabled'; onPress: () => void; status: 'idle' | 'pending' };
  layout: 'large' | 'compact';
  unreadAction: { availability: 'enabled' | 'disabled'; onPress: () => void; status: 'idle' | 'pending' };
  t: TFunction;
}) => (
  <Accessory marginBottom={layout === 'large' ? theme.spacing['spacing.general.x2'] : 120}>
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      <Gradient
        from={theme.colors['surface.default']}
        fromOpacity={0}
        style={{ position: 'absolute', left: 0, right: 0, bottom: -30, height: 60 }}
        to={theme.colors['surface.default']}
      />
      <View
        style={{
          paddingHorizontal: theme.spacing['spacing.general.x4'],
          width: '100%',
          gap: theme.spacing['spacing.general.x2'],
          paddingTop: layout === 'large' ? theme.spacing['spacing.general.x2'] : 0,
        }}
      >
        <Button
          disabled={unreadAction.availability === 'disabled'}
          loading={unreadAction.status === 'pending'}
          onPress={unreadAction.onPress}
          variant="primary"
        >
          {t('markAsUnreadButton')}
        </Button>
        <Button
          disabled={archiveAction.availability === 'disabled'}
          loading={archiveAction.status === 'pending'}
          onPress={archiveAction.onPress}
          variant="primary"
        >
          {t('archiveButton')}
        </Button>
      </View>
    </View>
  </Accessory>
);

function getChannelSelectionAccessory({
  canArchive,
  canMarkAsUnread,
  isArchivingChannels,
  isLargeUI,
  isMarkingChannelsAsUnread,
  isSelecting,
  onArchiveManyPress,
  onMarkAsUnreadPress,
  selectedChannels,
  t,
}: {
  canArchive: boolean;
  canMarkAsUnread: boolean;
  isArchivingChannels: boolean;
  isLargeUI: boolean;
  isMarkingChannelsAsUnread: boolean;
  isSelecting: boolean | undefined;
  onArchiveManyPress: () => void;
  onMarkAsUnreadPress: (urls: string[]) => void;
  selectedChannels: string[];
  t: TFunction;
}) {
  if (!isSelecting) return undefined;
  return (
    <ChannelSelectionAccessory
      archiveAction={{
        availability: canArchive ? 'enabled' : 'disabled',
        onPress: onArchiveManyPress,
        status: isArchivingChannels ? 'pending' : 'idle',
      }}
      layout={isLargeUI ? 'large' : 'compact'}
      t={t}
      unreadAction={{
        availability: canMarkAsUnread ? 'enabled' : 'disabled',
        onPress: () => onMarkAsUnreadPress(selectedChannels),
        status: isMarkingChannelsAsUnread ? 'pending' : 'idle',
      }}
    />
  );
}

function getChannelSelectionAvailability(items: ChannelListItemType[], selectedChannels: string[]) {
  const itemsByUrl = new Map(items.map((item) => [item.channel_url, item]));
  const canMarkAsUnread = selectedChannels.some((url) => {
    const item = itemsByUrl.get(url);
    return item && !isGroupHeader(item) && item.unread_message_count === 0;
  });
  const canArchive =
    selectedChannels.length > 0 &&
    !selectedChannels.some((url) => {
      const item = itemsByUrl.get(url);
      return item && !isGroupHeader(item) && item.unread_message_count > 0;
    });
  return { canArchive, canMarkAsUnread };
}

function getConditionalListComponents({
  isLoading,
  itemCount,
  ListEmptyComponent,
  ListFooterComponent,
  ListHeaderComponent,
  search,
}: Pick<ChannelListProps, 'ListEmptyComponent' | 'ListFooterComponent' | 'ListHeaderComponent' | 'search'> & {
  isLoading: boolean;
  itemCount: number;
}) {
  return {
    empty: isLoading ? <ActivityIndicator /> : ListEmptyComponent,
    footer: itemCount >= 20 && !search ? ListFooterComponent : undefined,
    header: itemCount || search ? ListHeaderComponent : undefined,
  };
}

const ChannelList = function ChannelList({
  ListEmptyComponent,
  ListHeaderComponent,
  ListFooterComponent,
  search,
  archived,
  isLoadingState,
  isSelecting,
  exitEditMode,
  listingId,
  userId,
  count: serverCount,
  onGroupHeaderPress,
  ...rest
}: ChannelListProps) {
  const navigation = useNavigation<'one-channel'>();
  const currentRoute = useRoute();
  const { isSmallUI, isLargeUI } = useUISize();

  const channelsQuery = useAllChannels({ search, archived, listingId, userId }, { throwOnError: true });
  const { t } = useTranslation('all-channels');

  const { selectedChannels: memoizedSelectedChannels, setSelectedChannels } = useChannelSelectionState(isSelecting);

  const onEndReached = async () => {
    if (channelsQuery.hasNextPage && !channelsQuery.isFetchingNextPage) {
      await channelsQuery.fetchNextPage();
    }
  };

  const setChannelsReadStatus = useSetAtom(optimisticChannelsReadStatusAtom);

  const flatItems = channelsQuery.data?.pages.map((page) => page.results).flat() ?? [];

  const flatChannels = flatItems.filter((item): item is Channel => !isGroupHeader(item));
  const items = getChannelListItems(flatItems, isSelecting, memoizedSelectedChannels);

  const insets = useSafeAreaInsets();

  const headerTitle = () => (
    <View style={{ backgroundColor: theme.colors['white-label.header.bg'] }}>
      <ChannelListHeader userId={userId} listingId={listingId} serverCount={serverCount} />
    </View>
  );

  useNavigationOptions(navigation, { header: headerTitle }, (!listingId && !userId) || isLargeUI);

  // When exiting the screen, update the recent channels list to clear any archived channels
  const [archivedChannels, setArchivedChannels] = useState(0);

  const onArchive = () => setArchivedChannels((prev) => prev + 1);
  const onArchiveMany = () => setArchivedChannels((prev) => prev + memoizedSelectedChannels.length);
  const onUnarchive = () => setArchivedChannels((prev) => prev - 1);

  const { mutate: markChannelAsRead } = useMarkChannelAsRead({
    onSuccess: (_, variables) => {
      setChannelsReadStatus((prev) => ({ ...prev, [variables]: true }));
      setOneChannelOpen(flatChannels.find((channel) => channel.channel_url === variables) || null);
    },
  });

  const { mutate: archiveManyChannels, isPending: isArchivingChannels } = useBatchArchiveChannels({
    onSuccess: () => {
      onArchiveMany();
      exitEditMode?.();
      setSelectedChannels([]);
    },
  });
  const onArchiveManyPress = () => {
    archiveManyChannels(memoizedSelectedChannels);
  };

  const { setHideConvo, setOneChannelOpen } = useMessageFooterContext();
  const onPress = (url: string, _unread_message_count: number) => {
    setChannelsReadStatus((prev) => ({
      ...prev,
      [url]: true,
    }));
    markChannelAsRead(url);
    if (currentRoute.name === 'one-channel' || isSmallUI) {
      navigation.navigate('one-channel', { url, userId, listingId, count: serverCount }, { pop: true });
    } else {
      setHideConvo(false);
    }
  };

  const { mutate: markChannelsAsUnread, isPending: isMarkingChannelsAsUnread } = useBatchUnreadChannels({
    onSuccess: () => {
      exitEditMode?.();
      setSelectedChannels([]);
      resetChannelsReadStatusAtom();
    },
  });

  const onMarkAsUnreadPress = (urls: string[]) => {
    // Optimistically mark channels as unread
    setChannelsReadStatus((prev) => {
      const newState = { ...prev };
      urls.forEach((u) => {
        newState[u] = false;
      });
      return newState;
    });
    // Send request to mark channels as unread
    markChannelsAsUnread(urls);
  };

  const resetChannelsReadStatusAtom = useResetAtom(optimisticChannelsReadStatusAtom);

  useEffect(() => {
    const onUnmount = () => {
      if (archivedChannels > 0) {
        void channelsQuery.refetch();
      }
      resetChannelsReadStatusAtom();
    };
    return navigation.addListener('beforeRemove', onUnmount);
  }, [archivedChannels, channelsQuery, navigation, resetChannelsReadStatusAtom]);

  useOnFocus(() => {
    // Always refetch Recent channels, when going back
    if (archived) return;
    void channelsQuery.refetch();
  });

  useTopLoadingIndicator(channelsQuery, isLoadingState);

  const userinfo = useAtomValue(userInfoAtom);

  const firstConciergeUrl = items.find(
    (item) =>
      !isGroupHeader(item) &&
      item.custom_type === CustomChannelType.Listing &&
      !!item.needMatch &&
      !!userinfo?.id &&
      item.listingUserId === userinfo.id &&
      item.listing?.type === ListingType.request,
  )?.channel_url;

  const { startTutorial, hasCompletedTutorial } = useTutorials();
  const [tutorialShown, setTutorialShown] = useState(false);
  useEffect(() => {
    const showTutorial =
      isMobileNative &&
      !archived &&
      !tutorialShown &&
      firstConciergeUrl &&
      !hasCompletedTutorial(Tutorial.Conciergerie);
    if (!showTutorial) return;
    const timeout = setTimeout(() => {
      if (!navigation.isFocused()) return;
      setTutorialShown(true);
      startTutorial(Tutorial.Conciergerie);
    }, 300);
    return () => clearTimeout(timeout);
  }, [archived, firstConciergeUrl, hasCompletedTutorial, navigation, startTutorial, tutorialShown]);

  const separator = () => <Column marginTop="spacing.general.x4" />;

  const { canArchive, canMarkAsUnread } = getChannelSelectionAvailability(items, memoizedSelectedChannels);

  const listHorizontalGutter = isLargeUI ? Math.max(theme.spacing['spacing.general.x4'], insets.left, insets.right) : 0;
  const conditionalListComponents = getConditionalListComponents({
    isLoading: channelsQuery.isLoading,
    itemCount: items.length,
    ListEmptyComponent,
    ListFooterComponent,
    ListHeaderComponent,
    search,
  });
  const selectionAccessory = getChannelSelectionAccessory({
    canArchive,
    canMarkAsUnread,
    isArchivingChannels,
    isLargeUI,
    isMarkingChannelsAsUnread,
    isSelecting,
    onArchiveManyPress,
    onMarkAsUnreadPress,
    selectedChannels: memoizedSelectedChannels,
    t,
  });

  const handleGroupHeaderPress = (item: ChannelGroupHeader) => {
    if (onGroupHeaderPress) {
      const filter =
        item.type === 'user_group'
          ? { userId: item.userId, count: item.count }
          : { listingId: item.listingId, count: item.count };
      onGroupHeaderPress(filter);
      return;
    }
    if (currentRoute.name === 'all-channels') {
      if (item.type === 'user_group') {
        navigation.push('all-channels', { userId: item.userId, count: item.count, archiveTab: archived });
      } else {
        navigation.push('all-channels', { listingId: item.listingId, count: item.count, archiveTab: archived });
      }
    } else {
      if (item.type === 'user_group') {
        navigation.navigate(
          'one-channel',
          { url: NO_CHANNEL_SELECTED, userId: item.userId, count: item.count },
          { pop: true },
        );
      } else {
        navigation.navigate(
          'one-channel',
          {
            url: NO_CHANNEL_SELECTED,
            listingId: item.listingId,
            count: item.count,
          },
          { pop: true },
        );
      }
    }
  };

  return (
    <ListPage
      {...rest}
      accessory={selectionAccessory}
      ItemSeparatorComponent={separator}
      ListEmptyComponent={conditionalListComponents.empty}
      ListFooterComponent={conditionalListComponents.footer}
      ListHeaderComponent={conditionalListComponents.header}
      data={items}
      keyExtractor={(item) => item.channel_url}
      listType={PageListComponentType.FLASH}
      onEndReached={() => void onEndReached()}
      onRefresh={channelsQuery.resetThenRefetch}
      refreshing={false}
      renderItem={({ item }) => {
        if (isGroupHeader(item)) {
          return (
            <GroupedChannels
              listingName={item.type === 'listing_group' ? item.listingName : undefined}
              userName={item.type === 'user_group' ? item.userName : undefined}
              count={item.count}
              picture={item.picture}
              isUserGroup={item.type === 'user_group'}
              onPress={() => handleGroupHeaderPress(item)}
              numUnread={item.unread_message_count}
            />
          );
        }
        return (
          <ChannelListItem
            channel={item}
            onArchive={onArchive}
            onPress={onPress}
            onUnarchive={onUnarchive}
            enableSwipeActions={!isSelecting}
            isSelecting={isSelecting}
            setSelectedChannels={setSelectedChannels}
            isFirstConcierge={item.channel_url === firstConciergeUrl}
            listingId={listingId}
            userId={userId}
            archived={archived}
            search={search}
          />
        );
      }}
      showsVerticalScrollIndicator={rest.showsVerticalScrollIndicator ?? false}
      contentContainerStyle={{
        paddingBottom: theme.spacing['spacing.general.x10'],
      }}
      style={{
        paddingLeft: listHorizontalGutter,
        paddingRight: listHorizontalGutter,
        ...(isLargeUI ? { width: '100%', maxWidth: '100%', alignSelf: 'stretch' as const } : {}),
      }}
      withPadding={isSmallUI}
    />
  );
};

export { ChannelList };
